import { useEffect, useState } from 'react';
import { CustomModal } from '../CustomModal';
import { Btn, InputField } from '../ui';
import { createPMTemplate } from '../../graphql/pmQueries';
import '../../css/PMEquipmentConfiguration.css';

interface SaveAsTemplateModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSaved: () => void;
    companyId: string;
    categoryId: string;
    categoryName: string;
    /** JSON-stringified matrix data (levels + rows + assignments) */
    matrixDataJson: string;
    userId?: string;
    userName?: string;
}

export const SaveAsTemplateModal = ({
    isOpen,
    onClose,
    onSaved,
    companyId,
    categoryId,
    categoryName,
    matrixDataJson,
    userId,
    userName,
}: SaveAsTemplateModalProps) => {
    const [templateName, setTemplateName] = useState('');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        setTemplateName('');
        setDescription('');
        setError('');
        setSaving(false);
    }, [isOpen]);

    const handleClose = () => {
        setTemplateName('');
        setDescription('');
        setError('');
        onClose();
    };

    const handleSave = async () => {
        setError('');
        if (!templateName.trim()) {
            setError('Template name is required.');
            return;
        }
        setSaving(true);
        try {
            await createPMTemplate({
                companyId,
                categoryId,
                categoryName,
                name: templateName.trim(),
                description: description.trim() || undefined,
                matrixData: matrixDataJson,
                createdByUserId: userId,
                createdByUserName: userName,
            });
            onSaved();
            handleClose();
        } catch (err) {
            console.error('Failed to save template:', err);
            setError('Failed to save template. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <CustomModal
            visible={isOpen}
            onDismiss={handleClose}
            header="Save as Template"
            footer={
                <div className="modal-footer-actions">
                    <Btn
                        variant="secondary-outlined"
                        buttonText="Cancel"
                        buttonOnClick={handleClose}
                        buttonDisabled={saving}
                    />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : 'Save Template'}
                        buttonOnClick={() => {
                            void handleSave();
                        }}
                        buttonDisabled={saving || !templateName.trim()}
                    />
                </div>
            }
        >
            {error && (
                <p className="pm-modal-error" role="alert">
                    {error}
                </p>
            )}

            <div className="pm-modal-fields">
                <InputField
                    label="Template name"
                    value={templateName}
                    onChange={setTemplateName}
                    placeholder="e.g. John Deere Loader — Standard PM"
                    autoFocus
                    ariaLabel="Template name"
                />
                <InputField
                    label="Category"
                    value={categoryName}
                    onChange={() => undefined}
                    disabled
                    ariaLabel="Category"
                />
                <InputField
                    label="Description (optional)"
                    value={description}
                    onChange={setDescription}
                    placeholder="What this template covers"
                    ariaLabel="Template description"
                />
            </div>

            <p className="pm-modal-hint">
                Available to <strong>every {categoryName} unit</strong> from the template picker. The current matrix
                (levels + rows) is saved, not the unit’s meta.
            </p>
        </CustomModal>
    );
};
